/**
 * dungeon-matrix.ts
 * Matrix rain effect for the Dungeon Master splash screen.
 * Follows the BlackBox Creative Suite splash pattern.
 *
 * Theme: dungeon runes, combat symbols, quest fragments
 * Accent: deep crimson with blue undertones
 */

export class DungeonMatrix {
    private canvas: HTMLCanvasElement | null = null
    private ctx: CanvasRenderingContext2D | null = null
    private drops: number[] = []
    private fontSize = 14
    private columns = 0
    private animationId: number | null = null
    private isActive = false

    private readonly accentColor = '#9B2335'
    private readonly accentGlow = 'rgba(155, 35, 53, 0.6)'

    private readonly matrix = [
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
        // Dungeon runes
        'D', 'U', 'N', 'G', 'E', 'O', 'N',
        'M', 'A', 'S', 'T', 'E', 'R',
        'C', 'A', 'V', 'E', 'R', 'N',
        'Q', 'U', 'E', 'S', 'T',
        'L', 'O', 'O', 'T',
        'T', 'R', 'A', 'P',
        'B', 'O', 'S', 'S',
        // Symbols
        '/', '\\', '|', '-', '_', '>', '<', '^',
        ':', '=', '+', '#', '*', '~',
        '(', ')', '[', ']', '{', '}',
        // Rune-like
        'α', 'β', 'γ', 'δ', 'λ', 'Σ', 'Ω', 'Ψ',
        '◊', '△', '▲', '●', '○', '◐',
        '░', '▒', '▓', '█',
        '∞', '∇', '⟨', '⟩',
    ]

    private readonly specialMessages = [
        'DUNGEON MASTER',
        'GROW YOUR DUNGEON',
        'CRAFT ENCOUNTERS',
        'SPAWN ADVENTURES',
        'INSIDE THE MOUNTAIN',
        'LIVING DUNGEONS',
        'BLACKBOX CREATIVE',
        'BUILD. TRAP. REWARD.',
        'EVERY MOUNTAIN HIDES',
        'EPIC HEROISM',
    ]

    init(): void {
        this.canvas = document.createElement('canvas')
        this.canvas.id = 'matrixRainCanvas'
        Object.assign(this.canvas.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: '1',
            opacity: '0.5',
        })

        const welcomeScreen = document.getElementById('welcomeScreen')
        if (welcomeScreen) {
            welcomeScreen.insertBefore(this.canvas, welcomeScreen.firstChild)
        }

        this.ctx = this.canvas.getContext('2d')
        this.resize()
        window.addEventListener('resize', () => this.resize())
    }

    private resize(): void {
        if (!this.canvas) return
        this.canvas.width = window.innerWidth
        this.canvas.height = window.innerHeight
        this.columns = Math.floor(this.canvas.width / this.fontSize)

        this.drops = []
        for (let i = 0; i < this.columns; i++) {
            this.drops[i] = Math.random() * -100
        }
    }

    start(): void {
        if (this.isActive) return
        this.isActive = true
        this.animate()
    }

    stop(): void {
        this.isActive = false
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId)
        }
        if (this.canvas?.parentNode) {
            this.canvas.remove()
        }
    }

    private animate(): void {
        if (!this.isActive || !this.ctx || !this.canvas) return

        // Fade trail
        this.ctx.fillStyle = 'rgba(10, 8, 12, 0.06)'
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)

        this.ctx.font = `${this.fontSize}px "Space Mono", monospace`

        for (let i = 0; i < this.drops.length; i++) {
            const char = this.matrix[Math.floor(Math.random() * this.matrix.length)]
            const x = i * this.fontSize
            const y = this.drops[i] * this.fontSize

            // Color varies - mostly accent, occasional bright
            if (Math.random() < 0.05) {
                this.ctx.fillStyle = '#e8c4c9' // Bright flash
            } else {
                this.ctx.fillStyle = this.accentColor
            }

            this.ctx.fillText(char, x, y)

            if (this.drops[i] * this.fontSize > this.canvas.height && Math.random() > 0.975) {
                this.drops[i] = 0
            }
            this.drops[i]++
        }

        // Rare special message
        if (Math.random() < 0.0015) {
            this.showSpecialMessage()
        }

        this.animationId = requestAnimationFrame(() => this.animate())
    }

    private showSpecialMessage(): void {
        if (!this.ctx || !this.canvas) return

        const message = this.specialMessages[Math.floor(Math.random() * this.specialMessages.length)]
        const x = Math.random() * (this.canvas.width - message.length * this.fontSize)
        const y = Math.random() * this.canvas.height

        this.ctx.save()
        this.ctx.font = `bold ${this.fontSize * 1.8}px "Space Mono", monospace`
        this.ctx.fillStyle = '#e8c4c9'
        this.ctx.shadowColor = this.accentGlow
        this.ctx.shadowBlur = 20
        this.ctx.fillText(message, x, y)
        this.ctx.restore()
    }
}

export const dungeonMatrix = new DungeonMatrix()
